(() => {
  const CATEGORY_TAGS = [
    "utställningskoppel",
    "halsband",
    "koppel",
    "sele",
    "namnbricka",
    "godispåse",
    "påshållare"
  ];

  const RECOMMENDATION_RULES = {
    halsband: [
      "koppel",
      "påshållare",
      "godispåse",
      "namnbricka",
      "sele"
    ],

    koppel: [
      "halsband",
      "påshållare",
      "godispåse",
      "namnbricka",
      "sele"
    ],

    sele: [
      "halsband",
      "koppel",
      "påshållare",
      "godispåse",
      "namnbricka"
    ],

    påshållare: [
      "halsband",
      "koppel",
      "sele",
      "godispåse",
      "namnbricka"
    ],

    godispåse: [
      "halsband",
      "koppel",
      "sele",
      "påshållare",
      "namnbricka"
    ],

    namnbricka: [
      "halsband",
      "koppel",
      "sele",
      "påshållare",
      "godispåse"
    ],

    utställningskoppel: [
      "utställningskoppel"
    ]
  };

  /*
   * Maul Mini ska bara kombineras med Mini-märkta produkter när
   * storleken faktiskt spelar roll. Universella tillbehör som
   * påshållare, godispåsar och namnbrickor fungerar för alla storlekar.
   */
  const MINI_SIZE_SENSITIVE_CATEGORIES = [
    "halsband",
    "koppel",
    "sele"
  ];

  let selectedMainProductColor = "";
  let scheduledFrame = null;

  const COLOR_OPTION_NAMES = [
    "färg",
    "color",
    "colour",
    "farbe",
    "farge"
  ];

  const normalize = (value) =>
    String(value || "")
      .toLocaleLowerCase("sv-SE")
      .trim();

  const toBoolean = (value) => value === "true";

  const getTags = (value) =>
    normalize(value)
      .split("|")
      .map((tag) => tag.trim())
      .filter(Boolean);

  const getCategory = (tags) =>
    CATEGORY_TAGS.find((category) => tags.includes(category)) || "";

  const isColorFieldset = (fieldset) => {
    if (!fieldset) return false;

    if (
      fieldset.classList.contains(
        "product-form__input--color"
      )
    ) {
      return true;
    }

    const handle = normalize(fieldset.dataset.handle)
      .replaceAll("-", " ");

    if (COLOR_OPTION_NAMES.includes(handle)) {
      return true;
    }

    const label = normalize(
      fieldset.querySelector(".form__label")?.textContent
    );

    return COLOR_OPTION_NAMES.some((name) =>
      label.startsWith(`${name}:`) || label === name
    );
  };

  const getColorFromFieldset = (fieldset) => {
    if (!isColorFieldset(fieldset)) return "";

    const select = fieldset.querySelector("select");

    if (select) {
      return normalize(
        select.selectedOptions[0]?.value || select.value
      );
    }

    return normalize(
      fieldset.querySelector('input[type="radio"]:checked')
        ?.value
    );
  };

  const getMainProductColor = () => {
    const selectors = Array.from(
      document.querySelectorAll("variant-selects")
    ).sort((a, b) => {
      const aSticky = a.matches(
        ".variant-selects--sticky, [data-sticky]"
      );
      const bSticky = b.matches(
        ".variant-selects--sticky, [data-sticky]"
      );

      return Number(aSticky) - Number(bSticky);
    });

    for (const selector of selectors) {
      const fieldsets = selector.querySelectorAll("fieldset");

      for (const fieldset of fieldsets) {
        const color = getColorFromFieldset(fieldset);

        if (color) return color;
      }
    }

    const activeSibling = document.querySelector(
      ".product-form__input--siblings label.active a"
    );

    return normalize(
      activeSibling?.dataset.colorName ||
        activeSibling?.getAttribute("aria-label") ||
        activeSibling?.getAttribute("title")
    );
  };

  const getSelectedColor = (section) => {
    if (selectedMainProductColor) {
      return selectedMainProductColor;
    }

    const mainProductColor = getMainProductColor();

    if (mainProductColor) return mainProductColor;

    return normalize(section.dataset.currentColor);
  };

  const candidateHasColor = (candidate, selectedColor) => {
    if (!selectedColor) return true;

    const colors = normalize(candidate.dataset.productColors);

    return colors.includes(`|${selectedColor}|`);
  };

  const selectCandidateColor = (
    candidateElement,
    selectedColor
  ) => {
    if (!candidateElement || !selectedColor) return;

    const card = candidateElement.querySelector(
      "[data-dl-color-card]"
    );

    if (!card) return;

    const swatches = Array.from(
      card.querySelectorAll("[data-dl-color-swatch]")
    );

    const matchingSwatch = swatches.find((swatch) => {
      return (
        normalize(swatch.dataset.colorName) === selectedColor
      );
    });

    if (!matchingSwatch) return;

    if (matchingSwatch.classList.contains("is-active")) {
      return;
    }

    matchingSwatch.click();
  };

  const getCandidateData = (element, selectedColor) => {
    const tags = getTags(element.dataset.productTags);
	const physicalStockColors = normalize(
	  element.dataset.productPhysicalStockColors
	);
	const backorderColors = normalize(
	  element.dataset.productBackorderColors
	);
	const hasPhysicalStock = selectedColor
	  ? physicalStockColors.includes(`|${selectedColor}|`)
	  : toBoolean(element.dataset.productPhysicalStock);
	const hasBackorder = selectedColor
	  ? backorderColors.includes(`|${selectedColor}|`)
	  : toBoolean(element.dataset.productBackorder);

    return {
      element,
      id: normalize(element.dataset.productId),
      vendor: normalize(element.dataset.productVendor),
      tags,
      category: getCategory(tags),
      isMini: toBoolean(element.dataset.productMini),
      isShowLeash: toBoolean(
        element.dataset.productShowLeash
      ),
      isAvailable: toBoolean(
        element.dataset.productAvailable
	  ),
	  stockRank: hasPhysicalStock ? 0 : hasBackorder ? 1 : 2
    };
  };

  const isMiniCompatible = ({
    candidate,
    currentIsMini
  }) => {
    if (!currentIsMini) {
      return !candidate.isMini;
    }

    if (
      MINI_SIZE_SENSITIVE_CATEGORIES.includes(
        candidate.category
      )
    ) {
      return candidate.isMini;
    }

    return true;
  };

  const chooseRecommendations = ({
    candidates,
    currentCategory,
    maximumProducts
  }) => {
    const priorities =
      RECOMMENDATION_RULES[currentCategory] || [];

    const selected = [];
    const selectedIds = new Set();
    const selectedCategories = new Set();

    /*
     * Första urvalet:
     * Välj högst en produkt från varje prioriterad kategori.
     */
    priorities.forEach((wantedCategory) => {
      if (selected.length >= maximumProducts) return;

      const match = candidates.find((candidate) => {
        return (
          candidate.category === wantedCategory &&
          !selectedIds.has(candidate.id) &&
          !selectedCategories.has(candidate.category)
        );
      });

      if (!match) return;

      selected.push(match);
      selectedIds.add(match.id);
      selectedCategories.add(match.category);
    });

    /*
     * Andra urvalet:
     * Fyll återstående platser med fler tillåtna produkter.
     */
    priorities.forEach((wantedCategory) => {
      if (selected.length >= maximumProducts) return;

      candidates
        .filter((candidate) => {
          return (
            candidate.category === wantedCategory &&
            !selectedIds.has(candidate.id)
          );
        })
        .forEach((candidate) => {
          if (selected.length >= maximumProducts) return;

          selected.push(candidate);
          selectedIds.add(candidate.id);
        });
    });

    return selected;
  };

  const cardRequests = new Map();
  const sectionVersions = new WeakMap();

  const loadCard = (element) => {
    if (element.querySelector('[data-dl-color-card]')) return Promise.resolve();
    const template = element.querySelector('template[data-dl-recommendation-card]');
    if (template) {
      element.appendChild(template.content.cloneNode(true));
      template.remove();
      return Promise.resolve();
    }
    const url = new URL(element.dataset.cardUrl, window.location.origin);
    url.searchParams.set('view', 'dl-recommendation-card');
    const key = url.href;
    if (!cardRequests.has(key)) {
      const request = fetch(key, { credentials: 'same-origin' })
        .then(response => {
          if (!response.ok) throw new Error('Recommendation unavailable');
          return response.text();
        })
        .then(html => {
          const parsed = new DOMParser().parseFromString(html, 'text/html');
          const card = parsed.querySelector('[data-dl-color-card]');
          if (!card) throw new Error('Recommendation card missing');
          return card;
        })
        .catch(error => { cardRequests.delete(key); throw error; });
      cardRequests.set(key, request);
    }
    return cardRequests.get(key).then(card => {
      if (!element.querySelector('[data-dl-color-card]')) {
        element.appendChild(card.cloneNode(true));
      }
    });
  };

  const renderSection = async (section) => {
    if (!section || !section.isConnected) return;

    const version = (sectionVersions.get(section) || 0) + 1;
    sectionVersions.set(section, version);
    const candidateElements = Array.from(
      section.querySelectorAll(
        "[data-dl-recommendation-candidate]"
      )
    );

    const maximumProducts =
      Number.parseInt(section.dataset.maximumProducts, 10) || 4;

    const currentVendor = normalize(
      section.dataset.currentProductVendor
    );

    const currentTags = getTags(
      section.dataset.currentProductTags
    );

    const currentCategory = getCategory(currentTags);

    const currentIsMini = toBoolean(
      section.dataset.currentProductMini
    );

    const currentIsShowLeash = toBoolean(
      section.dataset.currentProductShowLeash
    );

    const selectedColor = getSelectedColor(section);

    candidateElements.forEach((element) => {
      element.hidden = true;
      element.style.order = "";
    });

    if (!currentCategory) {
      section.hidden = true;
      return;
    }

    const validCandidates = candidateElements
	  .map((element) => getCandidateData(element, selectedColor))
      .filter((candidate) => {
        if (!candidate.isAvailable) return false;

        if (candidate.vendor !== currentVendor) return false;

        if (
          !isMiniCompatible({
            candidate,
            currentIsMini
          })
        ) {
          return false;
        }

        if (!candidate.category) return false;

        if (!candidateHasColor(candidate.element, selectedColor)) {
          return false;
        }

        /*
         * Utställningskoppel hålls helt separat från
         * vanliga produkter.
         */
        if (currentIsShowLeash) {
          return (
            candidate.category === "utställningskoppel"
          );
        }

        if (candidate.isShowLeash) return false;

        /*
         * För vanliga produkter rekommenderas aldrig
         * samma produktkategori.
         */
        if (candidate.category === currentCategory) {
          return false;
        }

        return true;
	  })
	  .sort((a, b) => a.stockRank - b.stockRank);

    const recommendations = chooseRecommendations({
      candidates: validCandidates,
      currentCategory,
      maximumProducts
    }).sort((a, b) => a.stockRank - b.stockRank);

    const loaded = await Promise.allSettled(
      recommendations.map(candidate => loadCard(candidate.element))
    );
    if (!section.isConnected || sectionVersions.get(section) !== version) return;
    let visibleCount = 0;
    recommendations.forEach((candidate, index) => {
      if (loaded[index].status !== 'fulfilled') return;
      candidate.element.hidden = false;
      candidate.element.style.order = String(index);
      selectCandidateColor(candidate.element, selectedColor);
      visibleCount += 1;
    });
    section.hidden = visibleCount === 0;
  };

  const renderAllSections = (container = document) => {
    if (
      container.matches?.("[data-dl-recommendations]")
    ) {
      renderSection(container);
    }

    container
      .querySelectorAll?.("[data-dl-recommendations]")
      .forEach(renderSection);
  };

  const scheduleRender = () => {
    if (scheduledFrame !== null) {
      window.cancelAnimationFrame(scheduledFrame);
    }

    scheduledFrame = window.requestAnimationFrame(() => {
      scheduledFrame = null;
      renderAllSections();
    });
  };

  const renderAfterProductUpdate = () => {
    scheduleRender();
    window.setTimeout(scheduleRender, 100);
    window.setTimeout(scheduleRender, 500);
  };

  /*
   * Reformations "combined listings" använder radio-/selectfält
   * med form-attribut. Fälten ligger inte inuti själva formuläret,
   * så closest('form') kan inte användas här.
   */
  document.addEventListener(
    "change",
    (event) => {
      const control = event.target.closest(
        "variant-selects input, variant-selects select"
      );

      if (!control) return;

      const fieldset = control.closest("fieldset");
      const selectedColor = getColorFromFieldset(fieldset);

      if (selectedColor) {
        selectedMainProductColor = selectedColor;
      }

      renderAfterProductUpdate();
    },
    true
  );

  /*
   * Stöd även temats separata product-siblings-block om det
   * används på någon annan produktmall.
   */
  const captureSiblingColor = (event) => {
    const siblingLink = event.target.closest(
      ".product-form__input--siblings a"
    );

    if (!siblingLink) return;

    const selectedColor = normalize(
      siblingLink.dataset.colorName ||
        siblingLink.getAttribute("aria-label") ||
        siblingLink.getAttribute("title")
    );

    if (selectedColor) {
      selectedMainProductColor = selectedColor;
    }

    renderAfterProductUpdate();
  };

  document.addEventListener(
    "pointerdown",
    captureSiblingColor,
    true
  );

  document.addEventListener(
    "click",
    captureSiblingColor,
    true
  );

  /*
   * Händelser som kan användas av Reformation eller Shopify
   * när produkt eller variant byts dynamiskt.
   */
  [
    "variant:change",
    "product:change",
    "product:variant-change",
    "theme:product:change"
  ].forEach((eventName) => {
    document.addEventListener(eventName, scheduleRender);
  });

  /*
   * Reformation kan ersätta hela eller delar av produktsidan
   * utan vanlig sidladdning. MutationObserver ser ändringen
   * och kör rekommendationsmotorn igen.
   */
  const observer = new MutationObserver((mutations) => {
    const productContentChanged = mutations.some(
      (mutation) => {
        if (mutation.type === "childList") {
          if (mutation.target.closest?.('[data-dl-recommendations]')) return false;
          return (
            mutation.addedNodes.length > 0 ||
            mutation.removedNodes.length > 0
          );
        }

        if (mutation.type === "attributes") {
          return (
            mutation.target.matches?.(
              "[data-dl-recommendations], " +
                "[data-color-name], " +
                'form[action*="/cart/add"]'
            ) || false
          );
        }

        return false;
      }
    );

    if (productContentChanged) {
      scheduleRender();
    }
  });

  const initialize = () => {
    renderAllSections();

    if (document.body) {
      observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: [
          "class",
          "aria-current",
          "aria-pressed",
          "data-current-color"
        ]
      });
    }
  };

  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      initialize,
      { once: true }
    );
  } else {
    initialize();
  }

  document.addEventListener(
    "shopify:section:load",
    (event) => {
      renderAllSections(event.target);
    }
  );
})();
